import { ArrowBackIos } from '@mui/icons-material';
import { Grid, MenuItem, Typography } from '@mui/material';
import React, { useState } from 'react';
import {
  AnalyteDetails,
  AnalytesTable,
  AnalytesTableProps,
  BloodReportCta,
  CtaState,
  Loading,
  ResultNav,
  RootineFooter,
} from '../components';
import { useAnalytes } from '../config/api/queries';
import { AnalyteReport } from '../types';
import { isMobile } from '../utils';

/**
 * Calculates the CTA state based on the analyte reports. User should
 * be shown a CTA if they are missing either a mineral or vitamin test.
 * @param analytes list of analyte reports
 * @returns a valid CtaState or null if no CTA should be shown
 */
const calculateCtaState = (analytes: AnalyteReport[]): CtaState | null => {
  const missingMineral = analytes.some((a) => {
    return (
      a.rootine_enabled &&
      a.result_history.length === 0 &&
      a.test_type === 'mineral'
    );
  });
  const missingVitamin = analytes.some((a) => {
    return (
      a.rootine_enabled &&
      a.result_history.length === 0 &&
      a.test_type === 'vitamin'
    );
  });

  if (missingMineral && missingVitamin) {
    return 'both';
  } else if (missingMineral) {
    return 'mineral';
  } else if (missingVitamin) {
    return 'vitamin';
  }

  return null;
};

export const BloodReport = (): JSX.Element => {
  const [selectedAnalyte, setSelectedAnalyte] =
    useState<AnalyteReport | null>();
  const [ctaState, setCtaState] = useState<CtaState | null>();
  const analyteReport = useAnalytes();

  if (analyteReport.isLoading) {
    return <Loading fullPage />;
  }

  const analytes = analyteReport.data
    ?.filter((analyte) => analyte.result_history.length > 0)
    .sort((a, b) => {
      if (a.result_history.length && b.result_history.length) {
        return a.sort_order - b.sort_order;
      }
      return b.result_history.length - a.result_history.length;
    });

  if (!analytes || !analytes.length) {
    return <BloodReportCta type={'both'} bgCover />;
  }

  if (!isMobile() && typeof selectedAnalyte === 'undefined') {
    setSelectedAnalyte(analytes[0]);
  }

  if (typeof ctaState === 'undefined') {
    setCtaState(calculateCtaState(analytes));
  }

  const analyteTableProps: AnalytesTableProps = {
    analytes: analytes,
    onClick: (i: number) => {
      if (analytes[i].result_history.length > 0) {
        setSelectedAnalyte(analytes[i]);
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      }
    },
  };

  return (
    <>
      <Grid
        container
        alignItems="center"
        justifyContent="center"
        rowSpacing={2}
        sx={{
          px: { xs: 0, md: 10 },
          marginTop: { xs: 7, md: '10' },
          mb: 10,
        }}
        className="debug"
      >
        {selectedAnalyte ? (
          <>
            <Grid
              item
              container
              xs={12}
              sx={{ display: { xs: 'none', lg: 'flex' } }}
            >
              <Grid item xs={12}>
                <ResultNav />
              </Grid>
              <Grid item container xs={12}>
                <Grid item xs={12} md={5} sx={{ background: '#F0F2F9' }}>
                  <AnalyteDetails {...{ analyte: selectedAnalyte }} />
                </Grid>
                <Grid item container xs={12} md={7}>
                  <Grid item xs={12}>
                    <AnalytesTable {...analyteTableProps} />
                  </Grid>
                  {ctaState && <BloodReportCta type={ctaState} />}
                </Grid>
              </Grid>
            </Grid>
            <Grid
              item
              xs={12}
              sx={{
                position: 'relative',
                background: '#F0F2F9',
                display: { md: 'inline', lg: 'none' },
              }}
            >
              <Grid item xs={12} sx={{ paddingY: 3, alignItems: 'center' }}>
                <MenuItem
                  onClick={() => {
                    setSelectedAnalyte(null);
                    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                  }}
                >
                  <ArrowBackIos />
                  <Typography
                    sx={{ cursor: 'pointer', fontSize: 16, opacity: 0.5 }}
                  >
                    {' BACK'}
                  </Typography>
                </MenuItem>
              </Grid>
              <Grid item xs={12}>
                <AnalyteDetails {...{ analyte: selectedAnalyte }} />
              </Grid>
            </Grid>
          </>
        ) : (
          <>
            <Grid item xs={12}>
              <ResultNav />
            </Grid>
            <Grid item xs={12}>
              <AnalytesTable {...analyteTableProps} />
              {ctaState && <BloodReportCta type={ctaState} />}
            </Grid>
          </>
        )}
      </Grid>
      <Grid item xs={12}>
        <RootineFooter />
      </Grid>
    </>
  );
};
